import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const method = params.get("method");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const cleared = useRef(false);

  useEffect(() => {
    if (method === "paypal") { setState("paid"); return; }
    if (!sessionId) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setState("pending"); return; }
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          setState("paid");
        } else if (data.status === "expired") {
          setState("error");
        } else {
          setTimeout(poll, 2000);
        }
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, method]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center">
      {state === "checking" && (
        <div data-testid="success-checking">
          <Loader2 className="mx-auto text-[#E63946] animate-spin mb-6" size={48} />
          <h1 className="font-heading text-4xl font-900 uppercase tracking-tight">Confirming Payment…</h1>
          <p className="text-[#A1A1AA] mt-3">Hold tight, we're verifying your order.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="success-paid">
          <CheckCircle2 className="mx-auto text-[#C5A059] mb-6" size={56} />
          <h1 className="font-heading text-5xl font-900 uppercase tracking-tight">You're On The Grid!</h1>
          <p className="text-[#A1A1AA] mt-4">Payment confirmed. Your gear is being prepared. Keep moving forward.</p>
          <Link to="/shop" className="inline-block mt-8 bg-[#E63946] hover:bg-[#FF4D5A] text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 transition-colors">
            Continue Shopping
          </Link>
        </div>
      )}
      {state === "pending" && (
        <div data-testid="success-pending">
          <Loader2 className="mx-auto text-[#A1A1AA] mb-6" size={48} />
          <h1 className="font-heading text-4xl font-900 uppercase tracking-tight">Payment Processing</h1>
          <p className="text-[#A1A1AA] mt-3">This is taking a moment. Check your email for confirmation.</p>
        </div>
      )}
      {state === "error" && (
        <div data-testid="success-error">
          <XCircle className="mx-auto text-[#E63946] mb-6" size={56} />
          <h1 className="font-heading text-4xl font-900 uppercase tracking-tight">Payment Not Completed</h1>
          <p className="text-[#A1A1AA] mt-3">Something went wrong or the session expired.</p>
          <Link to="/cart" className="inline-block mt-8 border border-white/20 hover:bg-white/10 text-white font-semibold uppercase tracking-wider text-sm px-8 py-4 transition-colors">
            Back To Cart
          </Link>
        </div>
      )}
    </div>
  );
}
